import { Button, Dialog, TextField, CircularProgress, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { ApiRequest } from "GlobalFunctions";
import ImageUploader from "elements/ImageUploader";
import BlogEditor from "components/Admin/Blog/BlogEditor"; // Ensure correct import path
import { CiCirclePlus } from "react-icons/ci";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AlertDialog from "elements/AlertDialog";
import axios from "axios";
import { ProjectConfig } from "Global";

export default function CompaniesEditor(props: any) {
  const { open, setOpen, loadList, company_id } = props;
  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = {
    company_id: company_id ? company_id : null,
    name: '',
    description: '',
    logo: '',
    website_url: '',
  };

  const [formValues, setFormValues] = useState(defaultValues);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<any>([]);
  const [blogsList, setBlogsList] = useState<any>([]);
  const [openBlogEditor, setOpenBlogEditor] = useState(false);
  const [selectedBlogId, setSelectedBlogId] = useState<number | null>(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [activeBlogId, setActiveBlogId] = useState<number | null>(null);

  const loadCompanyBlogs = async () => {
    if (company_id) {
      await ApiRequest({
        url: '/blog/companyList',
        method: 'GET',
        query: { company_id },
        setResponse: (response: any) => {
          if (response.status === 'success') {
            setBlogsList(response.list);
          }
        }
      });
    }
  };

  useEffect(() => {
    if (open) {
      if (company_id) {
        // Load company details if editing
        ApiRequest({
          url: '/companies/details',
          method: 'GET',
          query: {
            company_id: company_id
          },
          setResponse: (response: any) => {
            setFormValues(response.data);
          }
        });
        loadCompanyBlogs();
      } else {
        // Reset form for new company
        setFormValues(defaultValues);
        setFiles([]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, company_id]);

  const isFormValid = () => {
    return (
      formValues.name !== ''
    );
  };

  const handleSubmit = async () => { 
    setLoading(true);

    const body = new FormData();
    body.append('formValues', JSON.stringify(formValues));
    if (files) {
      body.append("logo", files);
    }

    await ApiRequest({
      url: "/companies/addedit",
      method: 'POST',
      formData: true,
      body: body,
      setResponse: (response: any) => {
        enqueueSnackbar(company_id ? 'Empresa actualizada correctamente' : 'Empresa creada correctamente', { variant: 'success' });
        setOpen(false);
        loadList();
        setFormValues(defaultValues);
      }
    });

    setLoading(false);
  };

  const handleClose = () => {
    setFormValues(defaultValues);
    setFiles([]);
    setOpen(false);
  };

  // Reload local list of blogs if needed (when BlogEditor closes)
  const reloadBlogs = () => {
    loadCompanyBlogs();
    loadList(); // Also refresh the main companies list if desired
  };

  const handleDelete = (blog_id: number) => {
    setActiveBlogId(blog_id);
    setOpenAlert(true);
  };

  const deleteItem = () => {
    const body = {
      blog_id: activeBlogId
    };

    axios.post(ProjectConfig.api_url + "/blog/deleteItem", body)
      .then((response) => {
        if (response.data.status === "success") {
          enqueueSnackbar('Entrada de blog eliminada con éxito', { variant: 'success' });
          loadCompanyBlogs();
        }
      })
      .catch((err) => {
        console.log(err.data);
      })
      .then(() => {
        setOpenAlert(false);
      });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <div className="DialogContainer">
        <div className="ModuleWrapper">
          <h2>{company_id ? 'Editar empresa' : 'Añadir empresa'}</h2>
          
          <div className="input_group">
            <ImageUploader 
              className="company_logo_uploader"
              image={formValues.logo} 
              setImage={(newLogo) => setFormValues({ ...formValues, logo: newLogo })}
              setFiles={setFiles}
              label="Logo de la empresa" 
            />
          </div>
          <div className="input_group">
            <TextField
              label="Nombre"
              variant="outlined"
              fullWidth
              required
              value={formValues.name}
              onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
            />
          </div>
          <div className="input_group">
            <TextField
              label="Sitio web (URL)"
              variant="outlined"
              fullWidth
              placeholder="https://www.ejemplo.cl"
              value={formValues.website_url}
              onChange={(e) => setFormValues({ ...formValues, website_url: e.target.value })}
            />
          </div>
          <div className="input_group">
            <TextField
              label="Descripción"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={formValues.description}
              onChange={(e) => setFormValues({ ...formValues, description: e.target.value })}
            />
          </div>

          {company_id && (
        <div className="company_blogs_section">
          <div className="blogs_list_header">
            <h3>Entradas de esta empresa</h3>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setSelectedBlogId(null);
                setOpenBlogEditor(true);
              }}
              startIcon={<CiCirclePlus />}
            >
              Nueva
            </Button>
          </div>

          {blogsList.length > 0 ? (
            <div className="blog_list_wrapper">
              {blogsList.map((blog: any) => (
                <div key={blog.blog_id} className="blog_list_item">
                  {blog.title}
                  <div className="blog_actions">
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        setSelectedBlogId(blog.blog_id);
                        setOpenBlogEditor(true);
                      }}
                    >
                      Editar
                    </Button>
                    <IconButton
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={() => handleDelete(blog.blog_id)}
                    >
                      <DeleteOutlineOutlinedIcon />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No hay blogs asociados</p>
          )}
         
        </div>
      )}

        </div>
      </div>
      
      <BlogEditor
        open={openBlogEditor}
        setOpen={setOpenBlogEditor}
        loadList={reloadBlogs}
        blog_id={selectedBlogId}
        source="companies"
        company_id={company_id}
      />

      <AlertDialog
        open={openAlert}
        setOpen={setOpenAlert}
        subtitle="¿Está seguro que desea eliminar esta entrada de blog?"
        send={deleteItem}
      />

      <div className="dialogButtons">
        <Button onClick={handleClose} variant="outlined">Cancelar</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={!isFormValid() || loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Guardando...' : (company_id ? 'Actualizar' : 'Crear')}
        </Button>
      </div>
    </Dialog>
  );
}
